import React from 'react';
import HeaderAdv2 from './HeaderAdv2';
import CTAButton from './CTAButton';
import TimelineEvent from './TimelineEvent';
import TestimonialCard from './TestimonialCard';
import FeaturePoint from './FeaturePoint';
import { CheckCircleIcon, BookOpenIcon, CalendarIcon } from 'lucide-react';
const AdvertorialAdv2 = () => {
  return <div className="max-w-4xl mx-auto bg-white shadow-md font-serif border border-gray-200" style={{
    fontFamily: 'Georgia, Cambria, "Times New Roman", serif'
  }} data-id="element-934">
      <HeaderAdv2 data-id="element-935" />
      <main className="px-4 sm:px-8 py-8" data-id="element-936">
        <div className="prose max-w-none" data-id="element-937">
          <div className="flex items-center text-xs text-gray-500 mb-4 border-b border-gray-200 pb-2" data-id="element-938">
            <div className="flex items-center mr-4" data-id="element-939">
              <BookOpenIcon className="h-3 w-3 mr-1" data-id="element-940" />
              <span data-id="element-941">Health Investigation Series</span>
            </div>
            <div className="flex items-center" data-id="element-942">
              <CalendarIcon className="h-3 w-3 mr-1" data-id="element-943" />
              <span data-id="element-944">
                Updated:{' '}
                {(() => {
                const yesterday = new Date();
                yesterday.setDate(yesterday.getDate() - 1);
                return yesterday.toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric'
                });
              })()}
              </span>
            </div>
          </div>

          <p className="text-lg font-medium text-gray-700 mb-6 leading-relaxed border-l-4 border-gray-800 pl-4" data-id="element-945">
            In my three decades of medical practice, I've developed a healthy
            skepticism toward pharmaceutical companies' claims about side
            effects.
          </p>
          <p className="mb-5 text-base leading-relaxed" data-id="element-946">
            I've watched "rare" side effects become common. I've seen patients
            suffer while companies deny patterns. I've filed reports that
            disappeared into regulatory black holes.
          </p>
          <p className="mb-6 text-base leading-relaxed" data-id="element-947">
            But nothing prepared me for what happened with semaglutide.
          </p>

          <div className="border border-gray-300 p-6 my-6 bg-gray-50 rounded shadow-sm" data-id="element-948">
            <p className="font-semibold mb-4 text-gray-900 text-base border-b border-gray-300 pb-3" data-id="element-949">
              Key Points:
            </p>
            <div className="space-y-3" data-id="element-950">
              <FeaturePoint data-id="element-951">
                4 in 10 patients on GLP-1 medications develop severe mental
                health issues
              </FeaturePoint>
              <FeaturePoint data-id="element-952">
                The problem isn't dopamine levels—it's disrupted brain pathways
              </FeaturePoint>
              <FeaturePoint data-id="element-953">
                FDA and EMA dismissed hundreds of reports for years
              </FeaturePoint>
              <FeaturePoint data-id="element-954">
                Only one compound restored normal brain function in imaging
                studies
              </FeaturePoint>
            </div>
          </div>

          <h2 className="text-2xl font-serif font-bold text-gray-900 mt-8 mb-5 border-b-2 border-gray-800 pb-3 flex items-center" data-id="element-955">
            <span className="inline-block w-4 h-4 bg-gray-800 mr-3 flex-shrink-0" data-id="element-956"></span>
            The Patient That Started My Investigation
          </h2>

          <p className="mb-5 text-base leading-relaxed" data-id="element-957">
            February 2022. My 7th patient on Semaglutide—I still remember her
            name, though I can't share it—came back for her 3-month follow-up.
          </p>

          <p className="mb-5 text-base leading-relaxed" data-id="element-958">
            The physical results were exactly what we'd hoped for: 18 pounds
            lost, A1C dropping, blood pressure improving.
          </p>

          <p className="mb-5 text-base leading-relaxed" data-id="element-959">
            But she was crying in my exam room.
          </p>

          <p className="mb-5 text-base leading-relaxed font-medium italic border-l-4 border-blue-500 pl-4 bg-blue-50 py-2 rounded-r" data-id="element-960">
            "Dr. Parham, I don't want to be alive anymore."
          </p>

          <p className="mb-5 text-base leading-relaxed" data-id="element-961">
            This was a 52-year-old woman with zero psychiatric history. Happy
            marriage, successful career, stable life. Three months earlier she'd
            been excited about finally finding a medication that worked.
          </p>

          <p className="mb-5 text-base leading-relaxed" data-id="element-962">
            Now, for some reason, she couldn't get out of bed. Couldn't feel joy
            about anything. Described herself as "a zombie going through the
            motions."
          </p>

          <p className="mb-5 text-base leading-relaxed" data-id="element-963">
            "When did this start?" I asked.
          </p>

          <p className="mb-5 text-base leading-relaxed" data-id="element-964">
            "About 3 weeks after I was last here."
          </p>

          <p className="mb-5 text-base leading-relaxed" data-id="element-965">
            I referred her to psychiatry and ran a full workup. But I didn't
            connect it to the medication. There were no reported cases yet.
            Depression has a thousand causes.
          </p>

          <p className="mb-6 text-base leading-relaxed" data-id="element-966">
            That position changed when it kept happening.
          </p>

          <h2 className="text-2xl font-serif font-bold text-gray-900 mt-8 mb-5 border-b-2 border-gray-800 pb-3 flex items-center" data-id="element-967">
            <span className="inline-block w-4 h-4 bg-gray-800 mr-3 flex-shrink-0" data-id="element-968"></span>
            The Pattern I Couldn't Ignore
          </h2>

          <p className="mb-5 text-base leading-relaxed" data-id="element-969">
            Over the next eight months, the reports kept coming:
          </p>

          <div className="border-l-4 border-red-500 pl-6 py-5 bg-red-50 rounded-r mb-6 shadow-sm" data-id="element-970">
            <p className="mb-4 italic text-gray-700 text-base leading-relaxed" data-id="element-971">
              "I've lost 30 pounds but I don't care about anything anymore."
            </p>
            <p className="mb-4 italic text-gray-700 text-base leading-relaxed" data-id="element-972">
              "I used to love my hobbies. Now nothing interests me."
            </p>
            <p className="mb-4 italic text-gray-700 text-base leading-relaxed" data-id="element-973">
              "I'm crying for no reason. This isn't me."
            </p>
            <p className="mb-0 italic text-gray-700 text-base leading-relaxed" data-id="element-974">
              "I had my first suicidal thought last week. I've never had that
              before."
            </p>
          </div>

          <p className="mb-5 text-base leading-relaxed" data-id="element-975">
            All of them: 3-6 weeks after starting semaglutide.
          </p>

          <p className="mb-5 text-base leading-relaxed" data-id="element-976">
            All of them: no prior psychiatric history.
          </p>

          <p className="mb-5 text-base leading-relaxed" data-id="element-977">
            All of them: the same cluster of symptoms—profound depression,
            inability to feel pleasure (anhedonia), complete loss of motivation.
          </p>

          <p className="mb-5 text-base leading-relaxed" data-id="element-978">
            By September 2022, I'd seen it in 43 patients out of roughly 100 I'd
            prescribed GLP-1 agonists to.
          </p>

          <p className="mb-6 text-base border border-red-500 bg-red-50 px-4 py-3 inline-block rounded font-medium shadow-sm" data-id="element-979">
            That's 43%. Not the "rare" side effect the label claimed.
          </p>

          <h2 className="text-2xl font-serif font-bold text-gray-900 mt-8 mb-5 border-b-2 border-gray-800 pb-3 flex items-center" data-id="element-980">
            <span className="inline-block w-4 h-4 bg-gray-800 mr-3 flex-shrink-0" data-id="element-981"></span>
            The Reports That Went Nowhere
          </h2>

          <p className="mb-5 text-base leading-relaxed" data-id="element-982">I wasn't alone.</p>

          <p className="mb-5 text-base leading-relaxed" data-id="element-983">
            Colleagues across the country—endocrinologists, primary care
            doctors, weight loss specialists—all reported the same pattern.
          </p>

          <p className="mb-5 text-base leading-relaxed" data-id="element-984">
            We reported it. Over and over.
          </p>

          <p className="mb-5 text-base leading-relaxed" data-id="element-985">
            FDA adverse event reports. EMA pharmacovigilance submissions.
          </p>

          <p className="mb-5 text-base leading-relaxed" data-id="element-986">
            The response? Essentially: "Interesting. We'll look into it."
          </p>

          <p className="mb-5 text-base leading-relaxed" data-id="element-987">
            Months passed. Nothing changed. No warnings updated. No
            acknowledgment.
          </p>

          <p className="mb-5 text-base leading-relaxed" data-id="element-988">
            Until July 2023, when the European Medicines Agency finally
            announced they were "reviewing" the safety of GLP-1 medications for
            mental health effects.
          </p>

          <div className="my-6" data-id="element-989">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/13MdfkBtUpDVWdD8c5bKPt/EMA_Reviewing.png" alt="EMA statement on ongoing review of GLP-1 receptor agonists" className="w-full rounded-lg shadow-md" data-id="element-990" />
          </div>

          <p className="mb-5 text-base leading-relaxed" data-id="element-991">
            By May 2024—ten months later—still nothing. No results. No
            conclusions. No warnings.
          </p>

          <p className="mb-5 text-base leading-relaxed" data-id="element-992">
            I remember the exact moment I decided to act. I was looking at a new
            patient's chart. She needed Semaglutide. Her A1C was dangerously
            high.
          </p>

          <p className="mb-5 text-base leading-relaxed" data-id="element-993">
            But I couldn't prescribe it anymore. I'd be gambling with the mental
            health of a human being once again, and I had no way to predict if
            she'd be in that 43% who'd end up severely depressed.
          </p>

          <p className="mb-6 text-base bg-yellow-100 border-l-4 border-yellow-500 px-4 py-3 inline-block rounded-r font-medium shadow-sm leading-relaxed" data-id="element-994">
            That's when I thought: If they're not going to solve this, someone
            has to.
          </p>

          <h2 className="text-2xl font-serif font-bold text-gray-900 mt-8 mb-5 border-b-2 border-gray-800 pb-3 flex items-center" data-id="element-995">
            <span className="inline-block w-4 h-4 bg-gray-800 mr-3 flex-shrink-0" data-id="element-996"></span>
            The 16-Month Investigation Begins
          </h2>

          <div className="sm:flex items-center gap-6 mb-6" data-id="element-997">
            <div className="sm:w-1/2 mb-6 sm:mb-0" data-id="element-998">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/tMQQsmfwszSqae1i9nukTB/Investigation_Begins.webp" alt="Medical research colleagues" className="w-full rounded-lg shadow-md" data-id="element-999" />
              <p className="text-xs text-gray-500 italic mt-2 text-center" data-id="element-1000">
                Dr. Parham, Dr. Kim, Dr. Mirnik, Dr. Nogales, Dr. Anderson, Dr.
                Hall - Graduation Ceremony - May 26, 1995
              </p>
            </div>
            <div className="sm:w-1/2" data-id="element-1001">
              <p className="mb-5 text-base leading-relaxed" data-id="element-1002">
                Early May 2024. I called five colleagues I'd trained
                with—endocrinologists I trusted completely.
              </p>

              <p className="mb-5 text-base leading-relaxed" data-id="element-1003">
                "I'm investigating this with or without you," I told them. "But
                I could use help."
              </p>

              <p className="mb-5 text-base leading-relaxed" data-id="element-1004">
                All five said yes immediately. They'd seen the same pattern.
                They were equally frustrated.
              </p>

              <p className="mb-0 text-base leading-relaxed" data-id="element-1005">
                We pooled funds—about $180,000 total from our own pockets. No
                pharmaceutical money. No grants. Just six doctors who refused to
                watch patients suffer while regulatory agencies moved at glacial
                speed.
              </p>
            </div>
          </div>

          <div className="mt-8 mb-8" data-id="element-1006">
            <h2 className="text-2xl font-serif font-bold text-gray-900 mb-6 border-b-2 border-gray-800 pb-3 flex items-center" data-id="element-1007">
              <span className="inline-block w-4 h-4 bg-gray-800 mr-3 flex-shrink-0" data-id="element-1008"></span>
              The Investigation Timeline
            </h2>

            <TimelineEvent title="Month 1: Recruiting Volunteers" data-id="element-1009">
              <p className="mb-4 text-base leading-relaxed" data-id="element-1010">
                We reached out to our patients who'd reported mental health
                changes on semaglutide. We posted in GLP-1 support groups on
                Facebook and Reddit. We contacted diabetes organizations.
              </p>

              <div className="my-6" data-id="element-1011">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/fbFEUrWT7w8B34KTMn2Qox/Gemini_Generated_Image_firg6efirg6efirg.png" alt="Medical research team meeting with volunteers" className="w-full rounded-lg shadow-md" data-id="element-1012" />
                <p className="text-xs text-gray-500 italic mt-2 text-center" data-id="element-1013">
                  First Group of 30 Volunteers - May 14, 2024
                </p>
              </div>

              <p className="mb-4 text-base leading-relaxed" data-id="element-1014">
                Within three weeks, we had 417 volunteers.
              </p>

              <p className="mb-0 text-base border border-yellow-400 bg-yellow-50 px-4 py-3 inline-block rounded font-medium shadow-sm leading-relaxed" data-id="element-1015">
                That number haunts me. It means this problem is enormous—far
                bigger than "rare."
              </p>
            </TimelineEvent>

            <TimelineEvent title="Months 2-5: The Dead Ends" data-id="element-1016">
              <p className="mb-4 text-base leading-relaxed" data-id="element-1017">
                Blood sugar fluctuations? No. Glucose levels were stable.
              </p>

              <p className="mb-4 text-base leading-relaxed" data-id="element-1018">
                Hormonal disruption from weight loss? No. Comprehensive panels
                showed nothing abnormal.
              </p>

              <p className="mb-4 text-base leading-relaxed" data-id="element-1019">
                Nutritional deficiencies? Some patients felt marginally better
                with supplementation, but core symptoms persisted.
              </p>

              <p className="mb-4 text-base leading-relaxed" data-id="element-1020">
                Dopamine and serotonin depletion? We tried L-DOPA, L-tyrosine,
                5-HTP, tryptophan—every supplement known to boost these
                chemicals.
              </p>

              <p className="mb-0 text-base leading-relaxed font-medium" data-id="element-1021">
                Nothing worked.
              </p>
            </TimelineEvent>

            <TimelineEvent title="Month 6: The Theory That Changed Everything" data-id="element-1022">
              <p className="mb-4 text-base leading-relaxed" data-id="element-1023">
                Dr. Sarah Kim, one of my colleagues, said something that shifted
                our entire approach:
              </p>

              <p className="mb-5 italic text-gray-700 text-base border-l-4 border-blue-500 pl-6 py-4 bg-blue-50 rounded-r leading-relaxed shadow-sm" data-id="element-1024">
                "What if the problem isn't the amount of dopamine—but how the
                brain processes it?"
              </p>

              <p className="mb-4 text-base leading-relaxed" data-id="element-1025">
                Let me explain this as simply as I can.
              </p>

              <p className="mb-4 text-base leading-relaxed" data-id="element-1026">
                Your brain has pathways—think of them like highways—that
                dopamine and serotonin travel through to create feelings of
                reward, pleasure, and motivation.
              </p>

              <p className="mb-4 text-base leading-relaxed" data-id="element-1027">
                When you eat something delicious, accomplish something, or spend
                time with loved ones, dopamine travels these pathways and you
                feel good.
              </p>

              <p className="mb-5 text-base border border-red-500 bg-red-50 px-4 py-3 inline-block rounded font-medium shadow-sm leading-relaxed" data-id="element-1028">
                Our theory: Semaglutide wasn't depleting dopamine. It was
                blocking the pathways.
              </p>

              <p className="mb-4 text-base leading-relaxed" data-id="element-1029">
                The dopamine was there. It just couldn't get where it needed to
                go.
              </p>

              <p className="mb-4 text-base leading-relaxed" data-id="element-1030">
                That's why adding more dopamine didn't work. It's like adding
                more cars to a gridlocked highway.
              </p>

              <p className="mb-0 text-base leading-relaxed" data-id="element-1031">
                But we needed proof.
              </p>
            </TimelineEvent>

            <TimelineEvent title="Months 6-10: The Method" data-id="element-1032">
              <p className="mb-4 text-base leading-relaxed" data-id="element-1033">
                We needed to see inside the brain while it was processing
                dopamine.
              </p>

              <p className="mb-4 text-base leading-relaxed" data-id="element-1034">
                PET scans would work, but obtaining radioactive isotopes without
                government approval proved impossible after five months of
                trying.
              </p>

              <p className="mb-4 text-base leading-relaxed" data-id="element-1035">
                Then Dr. Jovan Mirnik found a solution: pharmacological MRI—the
                "nudge and watch" method.
              </p>

              <div className="my-6" data-id="element-1036">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/hmNTHNLHJczXdCToQNu9Ke/MRI_usage.jpg" alt="MRI machine used for brain imaging research" className="w-full rounded-lg shadow-md" data-id="element-1037" />
                <p className="text-xs text-gray-500 italic mt-2 text-center" data-id="element-1038">
                  Dr. Mirnik's MRI office - MA, USA
                </p>
              </div>

              <p className="mb-4 text-base leading-relaxed" data-id="element-1039">
                We'd give subjects a drug that activates dopamine or serotonin
                pathways, then use functional MRI to watch their brain's
                response in real time.
              </p>

              <p className="mb-0 text-base leading-relaxed" data-id="element-1040">
                Normal pathways show predictable patterns. Disrupted pathways
                show blunted, delayed, or abnormal responses.
              </p>
            </TimelineEvent>

            <TimelineEvent title="The Striking Results" data-id="element-1041">
              <p className="mb-4 text-base leading-relaxed" data-id="element-1042">
                We tested 87 subjects on semaglutide with mental health
                symptoms.
              </p>

              <p className="mb-5 text-base border border-red-500 bg-red-50 px-4 py-3 inline-block rounded font-medium shadow-sm leading-relaxed" data-id="element-1043">
                87 out of 87 showed pathway dysregulation. Every. Single. One.
              </p>

              <p className="mb-4 text-base leading-relaxed" data-id="element-1044">
                Even subjects with mild symptoms showed the same disrupted
                patterns.
              </p>

              <p className="mb-4 text-base leading-relaxed" data-id="element-1045">
                Then we scanned 30 subjects on semaglutide who reported zero
                mental health issues.
              </p>

              <p className="mb-4 text-base leading-relaxed" data-id="element-1046">
                26 of them also showed pathway disruption, just to a lesser
                degree.
              </p>

              <div className="border border-yellow-400 p-6 my-5 bg-yellow-50 rounded shadow-sm" data-id="element-1047">
                <p className="mb-3 text-base font-semibold text-gray-900" data-id="element-1048">
                  The conclusion was inescapable:
                </p>
                <p className="text-base mb-0 leading-relaxed" data-id="element-1049">
                  Semaglutide disrupts dopamine and serotonin pathways in
                  virtually everyone. Whether you experience symptoms depends on
                  your individual brain chemistry and resilience.
                </p>
              </div>

              <p className="mb-0 text-base leading-relaxed" data-id="element-1050">
                That's why roughly 4 in 10 suffer severely—but the mechanism
                affects everyone.
              </p>
            </TimelineEvent>

            <TimelineEvent title="Finding the Solution" data-id="element-1051">
              <p className="mb-4 text-base leading-relaxed" data-id="element-1052">
                Now we knew the problem. But how do you fix disrupted brain
                pathways while someone still takes the medication they need?
              </p>

              <p className="mb-4 text-base leading-relaxed" data-id="element-1053">
                We tried psychiatric medications like bupropion and buspirone.
                Marginal improvement, but we'd be prescribing drugs just to
                counteract side effects. That felt wrong.
              </p>

              <p className="mb-4 text-base leading-relaxed" data-id="element-1054">
                We tried nootropics—lion's mane, phosphatidylserine, citicoline.
                No meaningful change after four weeks.
              </p>

              <p className="mb-4 text-base leading-relaxed" data-id="element-1055">
                We tested B vitamins, magnesium, omega-3s, curcumin, adaptogens.
                Nothing produced significant results.
              </p>

              <p className="mb-0 text-base leading-relaxed font-medium" data-id="element-1056">
                We'd spent $12,000 on supplements and $30,000 on imaging over
                four months. We were nowhere.
              </p>
            </TimelineEvent>

            <TimelineEvent title="Month 11: The Unexpected Solution" data-id="element-1057">
              <p className="mb-4 text-base leading-relaxed" data-id="element-1058">
                Dr. Amelia Nogales suggested traditional medicine—compounds used
                for centuries before we understood their mechanisms.
              </p>

              <p className="mb-4 text-base leading-relaxed" data-id="element-1059">
                We tested natural substances one by one with brain imaging.
              </p>

              <p className="mb-4 text-base leading-relaxed" data-id="element-1060">
                Ginkgo biloba. Nothing.
              </p>

              <p className="mb-4 text-base leading-relaxed" data-id="element-1061">
                Rhodiola. Nothing.
              </p>

              <p className="mb-4 text-base leading-relaxed" data-id="element-1062">
                Ashwagandha. Marginal at best.
              </p>

              <p className="mb-4 text-base leading-relaxed" data-id="element-1063">
                Then we tested saffron.
              </p>

              <p className="mb-4 text-base leading-relaxed" data-id="element-1064">
                Saffron—Crocus Sativus—the most expensive spice in the world—has
                been used in Persian and Indian medicine for depression for
                thousands of years.
              </p>

              <p className="mb-4 text-base leading-relaxed" data-id="element-1065">
                We'd almost skipped it. It seemed too simple. Too culinary.
              </p>

              <p className="mb-4 text-base leading-relaxed" data-id="element-1066">
                Dr. Nogales insisted.
              </p>

              <p className="mb-4 text-base leading-relaxed" data-id="element-1067">
                Our first subject took standardized saffron extract for two
                weeks. Then we ran the imaging.
              </p>

              <p className="mb-4 text-base leading-relaxed" data-id="element-1068">
                The difference was remarkable.
              </p>

              <div className="my-6" data-id="element-1069">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/eD8vs55rUsDnrw7QZRh1hy/Normalised_paTHWAYS.png" alt="Brain scan comparison showing dopamine transporter concentration in disrupted vs controlled subjects" className="w-full rounded-lg shadow-md" data-id="element-1070" />
                <p className="text-xs text-gray-500 italic mt-2 text-center" data-id="element-1071">
                  First Subject's Brain Image - March 27, 2025
                </p>
              </div>

              <p className="mb-4 text-base leading-relaxed" data-id="element-1072">
                Brain regions that had shown blunted responses were activating
                normally. Weak pathway connections were strengthening. Response
                timing and magnitude were normalizing.
              </p>

              <p className="mb-4 text-base leading-relaxed" data-id="element-1073">
                "Run it again," I said. "This has to be a fluke."
              </p>

              <p className="mb-4 text-base leading-relaxed" data-id="element-1074">
                We tested three more subjects. Same result.
              </p>

              <p className="mb-4 text-base leading-relaxed" data-id="element-1075">
                Ten more. Everyone showed improvement.
              </p>

              <p className="mb-0 text-base bg-yellow-100 border-l-4 border-yellow-500 px-4 py-3 inline-block rounded-r font-medium shadow-sm leading-relaxed" data-id="element-1076">
                For the first time in 11 months, we had something that worked.
              </p>
            </TimelineEvent>

            <TimelineEvent title="The Dosing Discovery" data-id="element-1077">
              <p className="mb-4 text-base leading-relaxed" data-id="element-1078">
                We created 12 groups of 7 subjects each, testing different forms
                of saffron over two months: powder, liquid, capsules, sublingual
                tablets, combined with black pepper, combined with fats,
                different extraction methods.
              </p>

              <p className="mb-4 text-base leading-relaxed" data-id="element-1079">
                The clear winner: standardized 4:1 saffron extract in capsule
                form—specifically standardized to contain exact amounts of
                crocin and safranal, the active compounds responsible for
                pathway support.
              </p>

              <p className="mb-4 text-base leading-relaxed" data-id="element-1080">
                By week 4, brain scans showed significantly normalized pathways.
              </p>

              <p className="mb-4 text-base leading-relaxed" data-id="element-1081">
                By week 8, subjects reported what imaging confirmed: depression
                lifting, anhedonia resolving, motivation returning.
              </p>

              <p className="mb-0 text-base leading-relaxed" data-id="element-1082">
                And they were still losing weight on their GLP-1 medications.
              </p>
            </TimelineEvent>

            <TimelineEvent title="Month 14: The Extra Mile" data-id="element-1083">
              <p className="mb-5 text-base leading-relaxed" data-id="element-1084">
                Once saffron proved effective for pathway support, we addressed
                other common issues: nausea, reflux, constipation, nutrient
                depletion.
              </p>

              <div className="border border-gray-300 p-6 my-5 bg-white rounded shadow-sm" data-id="element-1085">
                <p className="font-semibold mb-4 text-gray-900 text-base border-b border-gray-300 pb-3" data-id="element-1086">
                  We formulated a comprehensive supplement:
                </p>
                <div className="space-y-3" data-id="element-1087">
                  <FeaturePoint data-id="element-1088">
                    Saffron extract 4:1 (pathway support)
                  </FeaturePoint>
                  <FeaturePoint data-id="element-1089">
                    L-Theanine (calm focus, reduces anxiety without sedation)
                  </FeaturePoint>
                  <FeaturePoint data-id="element-1090">
                    Ginger extract (nausea, digestion)
                  </FeaturePoint>
                  <FeaturePoint data-id="element-1091">
                    Myo-inositol (dopamine receptor sensitivity)
                  </FeaturePoint>
                  <FeaturePoint data-id="element-1092">
                    Minerals: magnesium, zinc, copper (depleted by weight loss)
                  </FeaturePoint>
                  <FeaturePoint data-id="element-1093">
                    B-complex vitamins (neurotransmitter synthesis)
                  </FeaturePoint>
                </div>
              </div>

              <p className="mb-4 text-base leading-relaxed" data-id="element-1094">
                By June 28th, 2025—14 months after we started—every volunteer
                who stayed reported significant improvement.
              </p>

              <p className="mb-4 text-base leading-relaxed" data-id="element-1095">
                Depression lifted. Anhedonia gone. Motivation back. Joy
                returning.
              </p>

              <p className="mb-4 text-base leading-relaxed" data-id="element-1096">
                And they were still losing weight.
              </p>

              <p className="mb-0 text-base bg-yellow-100 border-l-4 border-yellow-500 px-4 py-3 inline-block rounded-r font-medium shadow-sm leading-relaxed" data-id="element-1097">
                We had brain imaging proof. The scans showed normalized pathway
                function. This wasn't placebo. This was measurable, reproducible
                restoration of brain chemistry.
              </p>
            </TimelineEvent>
          </div>

          <h2 className="text-2xl font-serif font-bold text-gray-900 mt-8 mb-5 border-b-2 border-gray-800 pb-3 flex items-center" data-id="element-1098">
            <span className="inline-block w-4 h-4 bg-gray-800 mr-3 flex-shrink-0" data-id="element-1099"></span>
            The Regulatory Reality
          </h2>

          <p className="mb-5 text-base leading-relaxed" data-id="element-1100">
            I filed comprehensive reports with the FDA and EMA. Sent our
            findings to pharmaceutical companies.
          </p>

          <p className="mb-5 text-base leading-relaxed" data-id="element-1101">
            The reality? We're a small group of doctors. Even if agencies take
            us seriously—and that's a big if—it will take months, or even years,
            for anything to change.
          </p>

          <p className="mb-5 text-base leading-relaxed" data-id="element-1102">
            Meanwhile, people are suffering now.
          </p>

          <p className="mb-5 text-base leading-relaxed" data-id="element-1103">
            Patients who need these medications for physical health but can't
            tolerate the mental cost. People who've stopped GLP-1 medications
            and regained weight because depression was too severe. People
            currently on Semaglutide experiencing that flat, empty feeling
            without knowing there's a biological reason.
          </p>

          <p className="mb-6 text-base bg-yellow-100 border-l-4 border-yellow-500 px-4 py-3 inline-block rounded-r font-medium shadow-sm" data-id="element-1104">
            We refused to wait for official channels.
          </p>

          <h2 className="text-2xl font-serif font-bold text-gray-900 mt-8 mb-5 border-b-2 border-gray-800 pb-3 flex items-center" data-id="element-1105">
            <span className="inline-block w-4 h-4 bg-gray-800 mr-3 flex-shrink-0" data-id="element-1106"></span>
            The First of Its Kind
          </h2>

          <p className="mb-5 text-base leading-relaxed" data-id="element-1107">
            After our research was complete, I faced a dilemma.
          </p>

          <p className="mb-5 text-base leading-relaxed" data-id="element-1108">
            We'd developed a formulation that worked. We had proof. But I'm a
            doctor, not a supplement manufacturer.
          </p>

          <p className="mb-5 text-base leading-relaxed" data-id="element-1109">
            Most companies I contacted weren't interested. Doses too high.
            Formulation too expensive. One literally said: "Why make something
            targeting just people on GLP-1 medications? Let drug manufacturers
            handle that small group."
          </p>

          <p className="mb-5 text-base leading-relaxed" data-id="element-1110">
            They were literally saying it was unprofitable to manufacture
            something that only people taking semaglutide would consume.
          </p>

          <p className="mb-6 text-base leading-relaxed" data-id="element-1111">Then I found Stefan.</p>

          <div className="sm:flex items-center gap-6 mb-6" data-id="element-1112">
            <div className="sm:w-1/2 mb-6 sm:mb-0" data-id="element-1113"></div>
            <div className="sm:w-1/2" data-id="element-1114">
              <p className="mb-5 text-base leading-relaxed" data-id="element-1115">
                Stefan Vass runs a small wellness company in Colorado. His
                website was basic, his marketing minimal.
              </p>

              <div className="my-6" data-id="element-1116">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/xqbkf64fxcM8BwnLzdHZRo/Screenshot_2025-11-09_at_14.19.12.png" alt="Email from Dr. Tom Parham to Stefan Vass" className="w-full rounded-lg shadow-md" data-id="element-1117" />
              </div>

              <p className="mb-5 text-base leading-relaxed" data-id="element-1118">
                I found his email and offered a call.
              </p>

              <p className="mb-5 text-base leading-relaxed" data-id="element-1119">
                Two hours later, my phone rang.
              </p>

              <p className="mb-0 italic text-gray-700 text-base border-l-4 border-blue-500 pl-4 bg-blue-50 py-2 rounded-r leading-relaxed" data-id="element-1120">
                "Tom? This is Stefan Vass. Send me everything. Every study,
                every scan, every ingredient ratio. If this actually works, I
                want to make it."
              </p>
            </div>
          </div>

          <p className="mb-5 text-base leading-relaxed" data-id="element-1121">
            Two days later, he called back. This time he sounded formal and
            professional:
          </p>

          <p className="mb-6 text-base italic text-gray-700 border-l-4 border-blue-500 pl-6 py-4 bg-blue-50 rounded-r-lg leading-relaxed shadow-sm" data-id="element-1122">
            "Dr. Parham, I've spent fourteen hours going through your data. This
            is legitimate science. I'm in."
          </p>

          <h2 className="text-2xl font-serif font-bold text-gray-900 mt-8 mb-5 border-b-2 border-gray-800 pb-3 flex items-center" data-id="element-1123">
            <span className="inline-block w-4 h-4 bg-gray-800 mr-3 flex-shrink-0" data-id="element-1124"></span>
            The Engineer's Approach
          </h2>

          <div className="sm:flex items-center gap-6 mb-6" data-id="element-1125">
            <div className="sm:w-1/2 mb-6 sm:mb-0" data-id="element-1126"></div>
            <div className="sm:w-1/2" data-id="element-1127">
              <p className="mb-5 text-base leading-relaxed" data-id="element-1128">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/hj5C9ABTUsEY7CaBVJxgSh/Stefan_Vass.jpg" alt="Stefan Vass - Former pharmaceutical formulation engineer" className="mb-2 w-full rounded-lg" data-id="element-1129" />
                <p className="text-xs text-gray-500 italic mb-6 text-center" data-id="element-1130">
                  Stefan Vass, Former Pharmaceutical Formulation Engineer -
                  Denver, CO
                </p>
                Stefan is 72, a former pharmaceutical formulation engineer who
                left the industry after watching too many corner-cutting
                decisions prioritize profit over efficacy.
              </p>

              <p className="mb-5 text-base leading-relaxed" data-id="element-1131">
                "I'll manufacture exactly what you developed," he told me. "No
                cost-cutting modifications. No dosage reductions. The exact
                ingredients and ratios from your study."
              </p>

              <p className="mb-5 text-base leading-relaxed" data-id="element-1132">
                "That sounds expensive," I said.
              </p>

              <p className="mb-5 text-base leading-relaxed border-l-4 border-blue-500 pl-6 py-4 bg-blue-50 rounded-r shadow-sm" data-id="element-1133">
                "It is. But if this fixes a real problem, people will reorder
                because it works—not because they're hoping this bottle might be
                different."
              </p>

              <p className="mb-5 text-base leading-relaxed" data-id="element-1134">
                Stefan invested his own money to set up production for what we'd
                developed.
              </p>

              <p className="mb-5 text-base leading-relaxed" data-id="element-1135">
                No investors. No pharmaceutical company backing. Just a retired
                engineer who saw legitimate science being ignored.
              </p>

              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/7uBcAWB76QykHq2qMZYiu7/Stefan_Vasss_Factory.png" alt="Pharmaceutical manufacturing facility" className="mt-6 mb-2 w-full rounded-lg" data-id="element-1136" />
              <p className="text-xs text-gray-500 italic mb-6 text-center" data-id="element-1137">
                Dr. Parham & Dr. Vass - First Meeting
                <br data-id="element-1138" />
                Pharmaceutical Facility, Denver, CO | July 7, 2025
              </p>

              <p className="mb-5 text-base leading-relaxed border-l-4 border-blue-500 pl-6 py-4 bg-blue-50 rounded-r shadow-sm" data-id="element-1139">
                "I've watched this industry either sell false hope or ignore the
                real problems people face for 50 years. That's the reason I
                retired into wellness," he told me.
              </p>

              <p className="mb-5 text-base leading-relaxed" data-id="element-1140">
                "That's exactly what triggered me and my dear colleagues on this
                journey," I said.
              </p>

              <p className="mb-0 text-base leading-relaxed" data-id="element-1141">
                "Right. So I'm willing to jump in this with you."
              </p>
            </div>
          </div>

          <p className="mb-6 text-base leading-relaxed" data-id="element-1142">
            And thus, the help and support came from a stranger with good
            intentions.
          </p>

          <h2 className="text-2xl font-serif font-bold text-gray-900 mt-8 mb-5 border-b-2 border-gray-800 pb-3 flex items-center" data-id="element-1143">
            <span className="inline-block w-4 h-4 bg-gray-800 mr-3 flex-shrink-0" data-id="element-1144"></span>
            Month 16: My First Patient Back
          </h2>

          <p className="mb-5 text-base leading-relaxed" data-id="element-1145">
            August 17th, 2025. 16th month in.
          </p>

          <p className="mb-5 text-base leading-relaxed" data-id="element-1146">
            A 54-year-old woman with type 2 diabetes sat in my exam room. Her
            A1C was 9.2. She was pre-hypertensive. She desperately needed GLP-1
            Agonist.
          </p>

          <p className="mb-5 text-base leading-relaxed" data-id="element-1147">
            For over a year, I'd been telling patients like her: "I can't
            prescribe this right now. The mental health risks are too high."
          </p>

          <p className="mb-5 text-base leading-relaxed" data-id="element-1148">
            But now I could say: "I can prescribe this. And I have something
            that addresses the side effects."
          </p>

          <p className="mb-5 text-base leading-relaxed" data-id="element-1149">
            I prescribed her Ozempic. And I paid for her first month of the
            supplement myself.
          </p>

          <p className="mb-5 text-base leading-relaxed" data-id="element-1150">
            She was my first patient back. I wanted to make sure she had every
            advantage.
          </p>

          <p className="mb-5 text-base leading-relaxed" data-id="element-1151">
            Three weeks later, she came back for a follow-up.
          </p>

          <p className="mb-5 text-base leading-relaxed" data-id="element-1152">
            "How are you feeling?" I asked, genuinely nervous despite proven
            results from 400+ subjects.
          </p>

          <p className="mb-5 text-base leading-relaxed" data-id="element-1153">
            "Great, actually. I'm losing weight—five pounds so far."
          </p>

          <p className="mb-5 text-base leading-relaxed" data-id="element-1154">
            "And your mental health? Energy, mood?" I asked.
          </p>

          <p className="mb-5 text-base leading-relaxed" data-id="element-1155">
            "Mmm... okay... I guess. I didn't think much about it when you
            mentioned the risk and gave me the supplement," she said, almost
            suspicious of my question.
          </p>

          <p className="mb-5 text-base leading-relaxed" data-id="element-1156">
            But I didn't feel awkward. I felt success. A different kind of
            success. This was my patient whom I'd potentially prevented from
            experiencing the darkness others had faced. I felt joy. Relief.
          </p>

          <p className="mb-6 text-base bg-yellow-100 border-l-4 border-yellow-500 px-4 py-3 inline-block rounded-r font-medium shadow-sm leading-relaxed" data-id="element-1157">
            Two months later: 16 pounds down, A1C dropping, zero mental health
            issues. That's when I knew we'd done something real.
          </p>

          <h2 className="text-2xl font-serif font-bold text-gray-900 mt-8 mb-5 border-b-2 border-gray-800 pb-3 flex items-center" data-id="element-1158">
            <span className="inline-block w-4 h-4 bg-gray-800 mr-3 flex-shrink-0" data-id="element-1159"></span>
            The Copy Problem
          </h2>

          <p className="mb-5 text-base leading-relaxed" data-id="element-1160">
            "Can't they just copy it?"
          </p>

          <p className="mb-5 text-base leading-relaxed" data-id="element-1161">
            Yes, they can. In fact, they did.
          </p>

          <p className="mb-5 text-base leading-relaxed" data-id="element-1162">
            Just a month after Zafira [the name Stefan gave the supplement] sold
            its very first item, Dr. Kim sent me a link to a website that had
            sold saffron supplements for years but had now adjusted their
            website to "target" patients taking semaglutide medications.
          </p>

          <p className="mb-5 text-base leading-relaxed" data-id="element-1163">
            And you know what? Let them.
          </p>

          <p className="mb-5 text-base leading-relaxed" data-id="element-1164">
            It only proves how severe this issue is. And as long as supplements
            do what they claim, we're happy the word spreads.
          </p>

          <p className="mb-6 text-base leading-relaxed" data-id="element-1165">
            Maybe, in this case, the problem will only be recognized when enough
            solutions exist.
          </p>

          <h2 className="text-2xl font-serif font-bold text-gray-900 mt-8 mb-5 border-b-2 border-gray-800 pb-3 flex items-center" data-id="element-1166">
            <span className="inline-block w-4 h-4 bg-gray-800 mr-3 flex-shrink-0" data-id="element-1167"></span>
            Gift From Stefan That Won My Heart
          </h2>

          <div className="sm:flex items-start gap-6 mb-6" data-id="element-1168">
            <div className="sm:w-1/2 mb-6 sm:mb-0" data-id="element-1169"></div>
            <div className="sm:w-1/2" data-id="element-1170">
              <p className="mb-5 text-base leading-relaxed" data-id="element-1171">
                A week after Stefan learned I was writing this very blog and
                would publish it online, he sent an email that surprised me:
              </p>

              <div className="my-6" data-id="element-1172">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/8m46Hg3umL3egUKGpGDNf1/Screenshot_2025-11-09_at_14.29.04.png" alt="Email from Stefan Vass" className="w-full rounded-lg shadow-md" data-id="element-1173" />
              </div>

              <p className="mb-5 text-base leading-relaxed" data-id="element-1174">
                I called him immediately.
              </p>

              <p className="mb-5 text-base leading-relaxed" data-id="element-1175">
                "Stefan, you can't be serious about selling at cost."
              </p>

              <p className="mb-0 text-base bg-yellow-100 border-l-4 border-yellow-500 px-4 py-3 rounded-r-lg font-medium shadow-sm leading-relaxed" data-id="element-1176">
                "Tom, this is a huge issue—a real issue that's not taken
                seriously. If your article helps thousands of people battle the
                depression that regulators won't even admit AND saves them
                money? That's worth more than profit to me. Once people test
                this and see it works, word spreads. This is marketing, just the
                honest kind."
              </p>
            </div>
          </div>

          <p className="mb-6 text-base leading-relaxed" data-id="element-1177">
            I volunteered to help create the website where we'd explain how the
            medication works and what problem each ingredient solves.
          </p>

          <div className="border-t-2 border-b-2 border-gray-300 py-10 my-8 bg-gradient-to-b from-gray-50 to-white" data-id="element-1178">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 text-center" data-id="element-1179">
              Check Availability for Stefan's offer at $54.90
            </h3>
            <div className="max-w-md mx-auto" data-id="element-1180">
              <CTAButton productName="Zafira Recovery Foundation" regularPrice="$84.90" salePrice="$54.90" discount="$30 OFF" trackingName="Zafira GLP-1 Support" url="https://www.zafiraorganics.com/" data-id="element-1181" />
            </div>
            <div className="text-xs text-gray-500 mt-4 text-center max-w-md mx-auto" data-id="element-1182">
              Includes satisfaction guarantee + Full consultation with Zafira
              Organics Team
            </div>
          </div>

          <p className="mb-6 text-base leading-relaxed" data-id="element-1183">
            I reached out to Zafira customers to verify feedback from external
            customers, not just my patients:
          </p>

          <div className="my-6 space-y-6" data-id="element-1184">
            <TestimonialCard name="Jennifer R., RN" location="44" duration="3 months" testimonial="Funny enough, Stefan told me, I am the very first buyer of Zafira. I'm a nurse. I test everything. My FitBit sleep scores were in the 60s on Mounjaro. After two weeks with Zafira: consistently in the 80s. My mood improved even more than my sleep." data-id="element-1185" />

            <TestimonialCard name="Lisa M." location="52" duration="2 months" testimonial="I was one dose away from stopping Wegovy because I couldn't handle the depression anymore. Started Zafira and within ten days I felt like myself again. Still losing weight, but I can actually enjoy my life now." data-id="element-1186" />

            <TestimonialCard name="David K." location="48" duration="3 weeks" testimonial="My doctor had no answers when I told him about the emotional flatness on Ozempic. He said 'maybe try therapy.' Zafira gave me back my motivation and joy. I wish my doctor had known about this from day one." data-id="element-1187" />
          </div>

          <h2 className="text-2xl font-serif font-bold text-gray-900 mt-8 mb-5 border-b-2 border-gray-800 pb-3 flex items-center" data-id="element-1188">
            <span className="inline-block w-4 h-4 bg-gray-800 mr-3 flex-shrink-0" data-id="element-1189"></span>
            The Safety Question Everyone Asks
          </h2>

          <p className="mb-5 text-base leading-relaxed" data-id="element-1190">
            "But is it safe to take this with my medication?"
          </p>

          <p className="mb-5 text-base leading-relaxed" data-id="element-1191">
            This was everyone's first question, including mine.
          </p>

          <p className="mb-6 text-base leading-relaxed" data-id="element-1192">
            Here's what we verified through our study:
          </p>

          <div className="border border-yellow-400 p-6 my-6 bg-yellow-50 rounded-lg shadow-sm" data-id="element-1193">
            <h3 className="text-lg font-semibold mb-5 text-gray-900" data-id="element-1194">
              Safety Assessment:
            </h3>

            <div className="space-y-3 mb-6" data-id="element-1195">
              <FeaturePoint data-id="element-1196">
                All ingredients are food-based nutrients, not pharmaceutical
                compounds
              </FeaturePoint>
              <FeaturePoint data-id="element-1197">
                Saffron has been used safely in traditional medicine for
                thousands of years
              </FeaturePoint>
              <FeaturePoint data-id="element-1198">
                No drug interactions with semaglutide, tirzepatide, or other
                GLP-1 medications
              </FeaturePoint>
              <FeaturePoint data-id="element-1199">
                Our 417 volunteers took it daily with their GLP-1 medications
                for months with zero adverse interactions
              </FeaturePoint>
            </div>

            <p className="text-gray-900 font-semibold mb-4 text-base" data-id="element-1200">
              In 16 months of testing with 417 volunteers:
            </p>

            <div className="space-y-3" data-id="element-1201">
              <div className="flex items-center text-gray-700" data-id="element-1202">
                <CheckCircleIcon className="h-5 w-5 text-green-600 mr-3 flex-shrink-0" data-id="element-1203" />
                <span className="text-base" data-id="element-1204">
                  Zero drug interactions with GLP-1 medications
                </span>
              </div>
              <div className="flex items-center text-gray-700" data-id="element-1205">
                <CheckCircleIcon className="h-5 w-5 text-green-600 mr-3 flex-shrink-0" data-id="element-1206" />
                <span className="text-base" data-id="element-1207">Zero serious adverse events</span>
              </div>
              <div className="flex items-center text-gray-700" data-id="element-1208">
                <CheckCircleIcon className="h-5 w-5 text-green-600 mr-3 flex-shrink-0" data-id="element-1209" />
                <span className="text-base" data-id="element-1210">Zero safety concerns</span>
              </div>
            </div>

            <p className="text-gray-700 mt-5 text-base leading-relaxed" data-id="element-1211">
              Only reported side effect: Mild nausea in 3 subjects during the
              first week (resolved by taking with food).
            </p>
          </div>

          <h3 className="text-xl font-serif font-bold text-gray-900 mt-10 mb-5" data-id="element-1212">
            Important Considerations:
          </h3>

          <p className="mb-5 text-base leading-relaxed" data-id="element-1213">
            While saffron is generally safe, there are specific situations where
            caution is needed:
          </p>

          <div className="border-l-4 border-red-500 pl-6 py-5 bg-red-50 rounded-r-lg mb-6 shadow-sm" data-id="element-1214">
            <p className="mb-4 text-base leading-relaxed" data-id="element-1215">
              <strong className="text-gray-900" data-id="element-1216">Bipolar disorder:</strong>{' '}
              Saffron may trigger excitability, impulsive behavior, and mood
              swings, potentially triggering manic episodes. If you have bipolar
              disorder, we do not recommend taking saffron in any form.
            </p>
            <p className="mb-4 text-base leading-relaxed" data-id="element-1217">
              <strong className="text-gray-900" data-id="element-1218">
                Psychiatric medications:
              </strong>{' '}
              If you're currently taking antidepressants (SSRIs, SNRIs),
              anti-anxiety medications, or other psychiatric drugs, please
              consult your doctor before adding saffron. While many people take
              saffron alongside these medications safely, there is a potential
              risk of serotonin syndrome (dangerously high serotonin levels)
              when combining multiple serotonin-affecting substances.
            </p>
            <p className="mb-4 text-base leading-relaxed" data-id="element-1219">
              <strong className="text-gray-900" data-id="element-1220">
                Other dopamine/serotonin supplements:
              </strong>{' '}
              If you're already taking supplements like 5-HTP, L-tyrosine, St.
              John's Wort, or other mood-supporting supplements, you should
              either choose one supplement or consult your doctor about
              combining them. Taking multiple supplements that affect the same
              pathways can lead to excessive neurotransmitter activity.
            </p>
            <p className="mb-0 text-base leading-relaxed" data-id="element-1221">
              <strong className="text-gray-900" data-id="element-1222">L-Theanine note:</strong> This
              formula contains L-Theanine, which is generally very safe and
              well-tolerated. However, if you're on blood pressure medications,
              consult your doctor as L-Theanine may have mild blood
              pressure-lowering effects.
            </p>
          </div>

          <p className="mb-6 text-base bg-yellow-100 border-l-4 border-yellow-500 px-4 py-3 inline-block rounded-r font-medium shadow-sm leading-relaxed" data-id="element-1223">
            Bottom line: This supplement is specifically designed to be safe
            with GLP-1 medications. However, if you're taking psychiatric
            medications or other mood-supporting supplements, have a
            conversation with your healthcare provider first to ensure it's
            appropriate for your specific situation.
          </p>

          <h2 className="text-2xl font-serif font-bold text-gray-900 mt-8 mb-5 border-b-2 border-gray-800 pb-3 flex items-center" data-id="element-1224">
            <span className="inline-block w-4 h-4 bg-gray-800 mr-3 flex-shrink-0" data-id="element-1225"></span>
            My Professional Verdict
          </h2>

          <p className="mb-6 text-base leading-relaxed" data-id="element-1226">
            After 16 months of investigation and $178,671 of our own money,
            here's what I know to be true:
          </p>

          <div className="space-y-3 mb-6 border border-yellow-400 p-6 bg-yellow-50 rounded shadow-sm" data-id="element-1227">
            <FeaturePoint data-id="element-1228">
              GLP-1 medications disrupt dopamine and serotonin pathways in
              virtually everyone taking them.
            </FeaturePoint>
            <FeaturePoint data-id="element-1229">
              About 4 in 10 people experience severe symptoms. But the mechanism
              is affecting most users to some degree.
            </FeaturePoint>
            <FeaturePoint data-id="element-1230">
              The problem isn't dopamine or serotonin levels—it's disrupted
              pathways. That's why standard supplements don't work.
            </FeaturePoint>
            <FeaturePoint data-id="element-1231">
              Only one compound restored normal pathway function in our imaging
              studies: high-dose saffron extract. We tested dozens of options.
              Saffron was the only one that worked.
            </FeaturePoint>
            <FeaturePoint data-id="element-1232">
              One manufacturer is producing the exact formulation from our
              study. With every ingredient at therapeutic doses, not token
              amounts.
            </FeaturePoint>
          </div>

          <h2 className="text-2xl font-serif font-bold text-gray-900 mt-8 mb-5 border-b-2 border-gray-800 pb-3 flex items-center" data-id="element-1233">
            <span className="inline-block w-4 h-4 bg-gray-800 mr-3 flex-shrink-0" data-id="element-1234"></span>
            The Opportunity Stefan Created
          </h2>

          <div className="border border-gray-400 p-6 my-6 bg-gray-50 rounded shadow-sm" data-id="element-1235">
            <p className="font-semibold mb-5 text-gray-900 text-base" data-id="element-1236">
              Stefan's offer for readers of this investigation:
            </p>
            <div className="space-y-3" data-id="element-1237">
              <div className="flex justify-between items-center" data-id="element-1238">
                <span className="text-base text-gray-700" data-id="element-1239">Regular price:</span>
                <span className="text-base font-medium text-gray-900" data-id="element-1240">
                  $84.90
                </span>
              </div>
              <div className="flex justify-between items-center" data-id="element-1241">
                <span className="text-base text-gray-700" data-id="element-1242">
                  Investigation reader price:
                </span>
                <span className="font-bold text-green-700 text-lg" data-id="element-1243">$54.90</span>
              </div>
              <div className="flex justify-between items-center" data-id="element-1244">
                <span className="text-base text-gray-700" data-id="element-1245">Valid for:</span>
                <span className="text-base text-gray-900" data-id="element-1246">
                  72 hours after publication
                </span>
              </div>
              <div className="flex justify-between items-center" data-id="element-1247">
                <span className="text-base text-gray-700" data-id="element-1248">
                  Available inventory:
                </span>
                <span className="text-base text-gray-900" data-id="element-1249">500 items</span>
              </div>
            </div>
          </div>

          <h2 className="text-2xl font-serif font-bold text-gray-900 mt-8 mb-5 border-b-2 border-gray-800 pb-3 flex items-center" data-id="element-1250">
            <span className="inline-block w-4 h-4 bg-gray-800 mr-3 flex-shrink-0" data-id="element-1251"></span>
            Our Personal Investment
          </h2>

          <p className="mb-5 text-base leading-relaxed" data-id="element-1252">
            At 52, after 30 years of medical practice, I've learned to be
            selective about what I endorse.
          </p>

          <p className="mb-5 text-base leading-relaxed" data-id="element-1253">
            I now recommend this supplement to every patient I put on GLP-1
            medications.
          </p>

          <p className="mb-5 text-base leading-relaxed" data-id="element-1254">
            Not because Stefan pays me (he doesn't—I've refused any financial
            arrangement).
          </p>

          <p className="mb-5 text-base leading-relaxed" data-id="element-1255">
            But because after 16 months of rigorous testing with brain imaging
            proof, it's the only thing I've seen that actually addresses the
            root cause of these side effects.
          </p>

          <p className="mb-5 text-base leading-relaxed" data-id="element-1256">
            My patients stay on their medications. They lose weight
            successfully. They keep their mental health.
          </p>

          <p className="mb-5 text-base leading-relaxed" data-id="element-1257">
            That's what medicine should be: solving problems, not forcing people
            to choose between physical health and mental wellbeing.
          </p>

          <div className="my-6" data-id="element-1258">
            <img src="https://cdn.magicpatterns.com/uploads/52Pmu1PcH4rcrWkpNKbfVV/Picture_of_the_whole_team_2B_Stefan.png" alt="Medical research team with Stefan Vass" className="w-full rounded-lg shadow-md" data-id="element-1259" />
            <p className="text-xs text-gray-500 italic mt-2 text-center" data-id="element-1260">
              Dr. Vass, Dr. Parham, Dr. Kim, Dr. Mirnik, Dr. Nogales, Dr.
              Anderson, Dr. Hall - Denver, CO - October 23, 2025
            </p>
          </div>

          <p className="mb-5 text-base leading-relaxed" data-id="element-1261">
            My colleagues—Dr. Sarah Kim, Dr. Jovan Mirnik, Dr. Amelia Nogales,
            Dr. Liam Anderson, Dr. Henry Hall—all recommend this because we've
            seen it work repeatedly, with measurable proof.
          </p>

          <p className="mb-6 text-base bg-yellow-100 border-l-4 border-yellow-500 px-4 py-3 inline-block rounded-r font-medium shadow-sm leading-relaxed" data-id="element-1262">
            After spending 16 months and our own savings on this research,
            watching it actually help people is the only reward we dreamt of.
          </p>

          <h2 className="text-2xl font-serif font-bold text-gray-900 mt-8 mb-5 border-b-2 border-gray-800 pb-3 flex items-center" data-id="element-1263">
            <span className="inline-block w-4 h-4 bg-gray-800 mr-3 flex-shrink-0" data-id="element-1264"></span>
            How to Access Stefan's Offer
          </h2>

          <p className="mb-5 text-base leading-relaxed" data-id="element-1265">
            Stefan created a page specifically for readers of this
            investigation:
          </p>

          <div className="border-t-2 border-b-2 border-gray-300 py-10 my-8 bg-gradient-to-b from-gray-50 to-white" data-id="element-1266">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 text-center" data-id="element-1267">
              Check Availability for Stefan's offer at $54.90
            </h3>
            <div className="max-w-md mx-auto" data-id="element-1268">
              <CTAButton productName="Zafira Recovery Foundation" regularPrice="$84.90" salePrice="$54.90" discount="$30 OFF" trackingName="Zafira GLP-1 Support" url="https://www.zafiraorganics.com/" data-id="element-1269" />
            </div>
            <div className="text-xs text-gray-500 mt-4 text-center max-w-md mx-auto" data-id="element-1270">
              Includes satisfaction guarantee + Full consultation with Zafira
              Organics Team
            </div>
          </div>

          <div className="border border-gray-300 p-6 my-6 bg-white rounded-lg shadow-sm" data-id="element-1271">
            <p className="font-semibold mb-4 text-gray-900 text-base" data-id="element-1272">
              Every order includes:
            </p>
            <div className="space-y-3" data-id="element-1273">
              <FeaturePoint data-id="element-1274">The exact formulation from our study</FeaturePoint>
              <FeaturePoint data-id="element-1275">60-day money back guarantee</FeaturePoint>
              <FeaturePoint data-id="element-1276">
                Information about the research behind it
              </FeaturePoint>
              <FeaturePoint data-id="element-1277">
                Direct email access to Stefan's team for questions
              </FeaturePoint>
            </div>
          </div>

          <p className="mb-6 text-base leading-relaxed" data-id="element-1278">
            That's the confidence that comes from actual science, not marketing
            claims.
          </p>

          <h2 className="text-2xl font-serif font-bold text-gray-900 mt-8 mb-5 border-b-2 border-gray-800 pb-3 flex items-center" data-id="element-1279">
            <span className="inline-block w-4 h-4 bg-gray-800 mr-3 flex-shrink-0" data-id="element-1280"></span>
            A Final Thought from Stefan
          </h2>

          <p className="mb-6 text-base leading-relaxed" data-id="element-1281">
            When I asked Stefan why he's doing this at 72 with a comfortable
            retirement ahead, his answer resonated:
          </p>

          <blockquote className="font-serif italic border-l-4 border-blue-500 pl-6 py-5 my-8 bg-blue-50 rounded-r text-gray-700 leading-relaxed shadow-sm" data-id="element-1282">
            "I spent fifty years in pharmaceutical formulation watching good
            science get buried by profit motives. When you showed me your
            research—real brain imaging, real patients, real results—I couldn't
            ignore it. If I can help people stay on medications they need while
            protecting their mental health, that matters more than maximizing my
            margins."
            <div className="text-sm mt-4 text-gray-600 not-italic font-sans" data-id="element-1283">
              — Stefan Vass, Former Pharmaceutical Formulation Engineer
            </div>
          </blockquote>

          <div className="border-t-2 border-gray-300 mt-8 pt-8" data-id="element-1284">
            <div className="flex items-center mb-6" data-id="element-1285">
              <div className="w-10 h-10 overflow-hidden rounded-full flex-shrink-0 mr-3 border-2 border-gray-300" data-id="element-1286">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/3QGeAdkaxHnkiYL33dUYdV/Tom_Parham.jpg" alt="Dr. Tom Parham" className="w-full h-full object-cover" data-id="element-1287" />
              </div>
              <p className="text-sm text-gray-700 leading-relaxed" data-id="element-1288">
                <span className="font-semibold text-gray-900" data-id="element-1289">
                  Dr. Tom Parham, MD, FACE
                </span>{' '}
                This investigation was entirely self-funded with no compensation
                from any mentioned company. Dr. Parham receives no financial
                benefit from product sales.
              </p>
            </div>

            <p className="text-sm text-gray-700 mb-4 bg-yellow-100 px-4 py-3 border-l-4 border-yellow-500 rounded-r leading-relaxed shadow-sm" data-id="element-1290">
              <strong data-id="element-1291">P.S.</strong> – If you're currently on Ozempic, Wegovy, or
              Mounjaro and experiencing depression, anhedonia, or emotional
              flatness—it's not in your head. We have brain imaging showing
              exactly why it's happening. And we have a solution that actually
              addresses the root cause.
            </p>

            <p className="text-sm text-gray-700 bg-yellow-100 px-4 py-3 border-l-4 border-yellow-500 rounded-r leading-relaxed shadow-sm" data-id="element-1292">
              <strong data-id="element-1293">P.P.S.</strong> – Stefan personally reviews feedback from
              every customer. When's the last time you could reach a supplement
              company founder directly? That tells you everything about the
              integrity behind his name.
            </p>
          </div>
        </div>
      </main>

      <div className="px-4 sm:px-8 py-6 border-t-2 border-gray-200 bg-gray-50" data-id="element-1294">
        <div className="max-w-4xl mx-auto text-[10px] text-gray-400 leading-relaxed space-y-2" data-id="element-1295">
          <p data-id="element-1296">
            These statements have not been evaluated by the Food and Drug
            Administration. This product is not intended to diagnose, treat,
            cure, or prevent any disease.
          </p>
          <p data-id="element-1297">
            Individual results may vary. Results described in testimonials and
            case studies are not typical and your experience may differ.
          </p>
          <p data-id="element-1298">
            This article is for informational purposes only and is not intended
            as a substitute for advice from your physician or other healthcare
            professional.
          </p>
          <p data-id="element-1299">
            Any references to scientific studies, researchers, or institutions
            are for informational context only and do not imply endorsement of
            this product.
          </p>
          <p data-id="element-1300">
            This is a paid advertisement for Zafira and is not an actual news
            article or blog. The story and opinions expressed here are for
            promotional purposes only.
          </p>
        </div>
      </div>

      <div className="bg-black h-3 w-full" data-id="element-1301"></div>
    </div>;
};
export default AdvertorialAdv2;